import * as React from 'react'
import { useMemo, useState } from 'react'
import { Button } from '@renderer/chat'
import { useSubmitSummaryFeedback } from '@renderer/chat/hooks'
import Checkbox from '@renderer/components/Checkbox'
import InputField from '@renderer/components/InputField'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import { Modal, useModalContext } from '../index'

const positive = [
  {
    question: "What did you like most about today's summary?",
    id: 'Liked most',
    options: [
      'Well organized and easy to follow',
      'Comprehensive and detailed',
      'Actionable insights highlighted',
      'Directly relevant to my goals and interests',
      'Other',
    ],
  },
  {
    question: 'How could we make it even better?',
    id: 'Feedback',
    options: [
      'Be more concise',
      'Add more context or examples',
      'Include visual aids (charts, bullet points)',
      'Other',
    ],
  },
]

const negative = [
  {
    question: 'What were the main issues with this summary?',
    id: 'Main issues',
    options: [
      'Missing important details',
      'Inaccurate or outdated information',
      'Too vague or high-level',
      'Irrelevant to my priorities',
      'Poorly organized or hard to follow',
      'Other',
    ],
  },
]

export const SummaryFeedbackModal = () => {
  const { context, close } = useModalContext('summary-feedback')
  const [selections, setSelections] = useState<Record<string, Array<string>>>({})
  const [others, setOthers] = useState<Record<string, string>>({})
  const submitFeedbackMutation = useSubmitSummaryFeedback()

  const questions = useMemo(() => {
    return context.rating === 'negative' ? negative : positive
  }, [context.rating])

  const onSubmit = () => {
    const eventProperties: Record<string, unknown> = {
      rating: context.rating,
    }
    for (const id of Object.keys(selections)) {
      const selected = selections[id]
      const otherReason = others[id]
      eventProperties[id] = [...selected, ...(otherReason ? [`Other Reason: ${otherReason}`] : [])]
    }

    close()
    AmplitudeRenderer.trackEvent('Summary Feedback', eventProperties)

    if (context.summary) {
      submitFeedbackMutation.mutate({
        summaryId: context.summary.id,
        feedback: JSON.stringify(eventProperties),
      })
    }
  }

  const isSubmitDisabled = useMemo(() => {
    return !Object.values(selections)
      .map((selection) => selection.length > 0)
      .every((selected) => selected)
  }, [selections])

  return (
    <Modal>
      <Modal.Content className="w-[500px]">
        <Modal.Header>
          <Modal.Title>Summary Feedback</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {questions.map((item) => {
            return (
              <div key={item.id} className="text-light/60 flex flex-col gap-2.5 text-sm leading-none">
                <span className="text-light/80 text-md mb-1 font-medium">{item.question}</span>
                {item.options.map((option) => {
                  return (
                    <Checkbox
                      key={`${item.id}-${option}`}
                      label={option}
                      checked={!!selections[item.id]?.includes(option)}
                      onChange={(e) => {
                        setSelections((prev) => {
                          return {
                            ...prev,
                            [item.id]: [
                              ...(prev[item.id]?.filter((o) => o !== option) ?? []),
                              ...(e.target.checked ? [option] : []),
                            ],
                          }
                        })
                      }}
                    />
                  )
                })}
                {selections[item.id]?.includes('Other') && (
                  <InputField
                    size="medium"
                    placeholder="Please specify"
                    value={others[item.id] ?? ''}
                    onChange={(e) =>
                      setOthers((prev) => {
                        return {
                          ...prev,
                          [item.id]: e.target.value,
                        }
                      })
                    }
                  />
                )}
              </div>
            )
          })}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="yellow-secondary" size="medium" disabled={isSubmitDisabled} onClick={onSubmit}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  )
}
